/**
 * API Client Service
 * Centralized service for making API calls to the backend
 * Handles authentication, error handling, and response formatting
 */

import { API_URL } from '../apiConfig';
import { supabase } from '../config/supabase';

/**
 * Get authentication token from Supabase session
 */
async function getAuthToken() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

/**
 * Get View As context from session storage
 */
function getViewAsContext() {
  try {
    const saved = sessionStorage.getItem('viewMode');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.active) {
        return {
          userId: parsed.userId,
          tenantId: parsed.tenantId
        };
      }
    }
  } catch (error) {
    // Ignore errors
  }
  return null;
}

/**
 * Create headers for API requests
 */
async function createHeaders(customHeaders = {}) {
  const token = await getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...customHeaders
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add tenant ID if available (for multi-tenant support)
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    // Check for View As context first
    const viewAsContext = getViewAsContext();
    if (viewAsContext) {
      // In View As mode - use viewed user's tenant
      if (viewAsContext.tenantId) {
        headers['X-Tenant-ID'] = viewAsContext.tenantId;
      }
      // Add View As user ID header for backend to handle
      if (viewAsContext.userId) {
        headers['X-View-As-User-ID'] = viewAsContext.userId;
      }
    } else {
      // Normal mode - get tenant from user profile
      const { data: profile } = await supabase
        .from('users')
        .select('tenant_id')
        .eq('id', user.id)
        .single();
      
      if (profile?.tenant_id) {
        headers['X-Tenant-ID'] = profile.tenant_id;
      }
    }
  }

  return headers;
}

/**
 * Handle API response
 */
async function handleResponse(response) {
  const contentType = response.headers.get('content-type');
  const isJson = contentType && contentType.includes('application/json');
  
  const data = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const error = new Error(data.error || data.message || `HTTP ${response.status}: ${response.statusText}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  // Backend returns { success: true, data: ..., meta: ... }
  // Extract data if present, otherwise return full response
  if (data && typeof data === 'object' && 'data' in data) {
    // For paginated responses, return both data and meta
    if ('meta' in data) {
      return {
        rows: data.data || [],
        total: data.meta?.total || data.data?.length || 0,
        page: data.meta?.page || 1,
        pageSize: data.meta?.limit || data.data?.length || 10,
        ...data.meta,
      };
    }
    return data.data;
  }

  return data;
}

/**
 * API Client Class
 */
class ApiClient {
  constructor(baseURL = API_URL) {
    this.baseURL = baseURL;
    this.apiBase = `${baseURL}/api/v1`;
  }

  /**
   * Generic request method
   */
  async request(endpoint, options = {}) {
    const url = endpoint.startsWith('http') 
      ? endpoint 
      : `${this.apiBase}${endpoint}`;

    const headers = await createHeaders(options.headers);
    
    const config = {
      ...options,
      headers: {
        ...headers,
        ...options.headers
      }
    };

    try {
      const response = await fetch(url, config);
      
      // Handle 401 Unauthorized - try to refresh token and retry once
      if (response.status === 401) {
        try {
          // Try to refresh the session
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const { data: { session: newSession }, error: refreshError } = await supabase.auth.refreshSession(session);
            if (!refreshError && newSession) {
              // Retry the request with new token
              const newHeaders = await createHeaders(options.headers);
              const retryConfig = {
                ...config,
                headers: {
                  ...newHeaders,
                  ...options.headers
                }
              };
              const retryResponse = await fetch(url, retryConfig);
              return await handleResponse(retryResponse);
            }
          }
        } catch (refreshErr) {
          console.warn('Token refresh failed:', refreshErr);
          // Continue to throw original 401 error
        }
      }
      
      return await handleResponse(response);
    } catch (error) {
      // Suppress connection errors (expected when backend is down)
      const errorMessage = error?.message || error?.toString() || '';
      const isConnectionError = errorMessage.includes('Failed to fetch') || 
                                errorMessage.includes('ERR_CONNECTION_REFUSED') ||
                                errorMessage.includes('NetworkError') ||
                                (error instanceof TypeError && errorMessage.includes('fetch'));
      
      // Only log non-connection errors in development
      if (!isConnectionError && import.meta.env.DEV) {
        console.warn(`API request failed: ${endpoint}`, errorMessage);
      }
      
      // For connection errors, create a more user-friendly error
      if (isConnectionError) {
        const connectionError = new Error('Backend server is not available');
        connectionError.isConnectionError = true;
        connectionError.originalError = error;
        throw connectionError;
      }
      
      throw error;
    }
  }

  /**
   * GET request
   */
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  /**
   * POST request
   */
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // ==================== AUTH ENDPOINTS ====================

  /**
   * Register new user
   */
  async register(userData) {
    return this.post('/auth/register', userData);
  }

  /**
   * Get current user
   */
  async getCurrentUser() {
    return this.get('/auth/me');
  }

  /**
   * Request password reset
   */
  async forgotPassword(email) {
    return this.post('/auth/forgot-password', { email });
  }

  /**
   * Reset password (admin)
   */
  async resetPassword(userId, newPassword) {
    return this.post('/auth/reset-password', { userId, newPassword });
  }

  /**
   * Resend verification email
   */
  async resendVerification(email) {
    return this.post('/auth/resend-verification', { email });
  }

  /**
   * Resend credentials email for a user
   */
  async resendUserCredentials(userId) {
    return this.post(`/users/${userId}/resend-credentials`);
  }

  // ==================== USER ENDPOINTS ====================

  /**
   * Get all users
   */
  async getUsers(params = {}) {
    return this.get('/users', params);
  }

  /**
   * Get user by ID
   */
  async getUserById(userId) {
    return this.get(`/users/${userId}`);
  }

  /**
   * Update user
   */
  async updateUser(userId, updates) {
    return this.put(`/users/${userId}`, updates);
  }

  /**
   * Delete user
   */
  async deleteUser(userId) {
    return this.delete(`/users/${userId}`);
  }

  /**
   * Activate user
   */
  async activateUser(userId) {
    return this.patch(`/users/${userId}/activate`);
  }

  /**
   * Deactivate user
   */
  async deactivateUser(userId) {
    return this.patch(`/users/${userId}/deactivate`);
  }

  /**
   * Get user profile
   */
  async getUserProfile(userId) {
    return this.get(`/users/${userId}/profile`);
  }

  /**
   * Update user profile
   */
  async updateUserProfile(userId, profileData) {
    return this.put(`/users/${userId}/profile`, profileData);
  }

  // ==================== APPOINTMENT ENDPOINTS ====================

  /**
   * Get all appointments
   */
  async getAppointments(params = {}) {
    return this.get('/appointments', params);
  }

  /**
   * Get appointment by ID
   */
  async getAppointmentById(appointmentId) {
    return this.get(`/appointments/${appointmentId}`);
  }

  /**
   * Create appointment
   */
  async createAppointment(appointmentData) {
    return this.post('/appointments', appointmentData);
  }

  /**
   * Update appointment
   */
  async updateAppointment(appointmentId, updates) {
    return this.put(`/appointments/${appointmentId}`, updates);
  }

  /**
   * Delete appointment
   */
  async deleteAppointment(appointmentId) {
    return this.delete(`/appointments/${appointmentId}`);
  }

  /**
   * Get upcoming appointments
   */
  async getUpcomingAppointments(limit = 10) {
    return this.get('/appointments/upcoming', { limit });
  }

  /**
   * Get past appointments
   */
  async getPastAppointments(limit = 10) {
    return this.get('/appointments/past', { limit });
  }

  /**
   * Get appointment statistics
   */
  async getAppointmentStats() {
    return this.get('/appointments/stats');
  }

  /**
   * Confirm appointment
   */
  async confirmAppointment(appointmentId) {
    return this.patch(`/appointments/${appointmentId}/confirm`);
  }

  /**
   * Start appointment
   */
  async startAppointment(appointmentId) {
    return this.patch(`/appointments/${appointmentId}/start`);
  }

  /**
   * Complete appointment
   */
  async completeAppointment(appointmentId) {
    return this.patch(`/appointments/${appointmentId}/complete`);
  }

  // ==================== MESSAGE ENDPOINTS ====================

  /**
   * Get all messages
   */
  async getMessages(params = {}) {
    return this.get('/messages', params);
  }

  /**
   * Get message by ID
   */
  async getMessageById(messageId) {
    return this.get(`/messages/${messageId}`);
  }

  /**
   * Send message
   */
  async sendMessage(messageData) {
    return this.post('/messages', messageData);
  }

  /**
   * Delete message
   */
  async deleteMessage(messageId) {
    return this.delete(`/messages/${messageId}`);
  }

  /**
   * Mark message as read
   */
  async markMessageAsRead(messageId) {
    return this.patch(`/messages/${messageId}/read`);
  }

  /**
   * Get conversations
   */
  async getConversations() {
    return this.get('/messages/conversations');
  }

  /**
   * Get conversation with user
   */
  async getConversation(userId, limit = 50) {
    return this.get(`/messages/conversation/${userId}`, { limit });
  }

  /**
   * Get unread count
   */
  async getUnreadCount() {
    return this.get('/messages/unread/count');
  }

  // ==================== PRESCRIPTION ENDPOINTS ====================

  /**
   * Get all prescriptions
   */
  async getPrescriptions(params = {}) {
    return this.get('/prescriptions', params);
  }

  /**
   * Get prescription by ID
   */
  async getPrescriptionById(prescriptionId) {
    return this.get(`/prescriptions/${prescriptionId}`);
  }

  /**
   * Create prescription
   */
  async createPrescription(prescriptionData) {
    return this.post('/prescriptions', prescriptionData);
  }

  /**
   * Update prescription
   */
  async updatePrescription(prescriptionId, updates) {
    return this.put(`/prescriptions/${prescriptionId}`, updates);
  }

  /**
   * Delete prescription
   */
  async deletePrescription(prescriptionId) {
    return this.delete(`/prescriptions/${prescriptionId}`);
  }

  /**
   * Get patient prescriptions
   */
  async getPatientPrescriptions(patientId) {
    return this.get(`/prescriptions/patient/${patientId}`);
  }

  /**
   * Get active prescriptions
   */
  async getActivePrescriptions() {
    return this.get('/prescriptions/active');
  }

  /**
   * Fulfill prescription
   */
  async fulfillPrescription(prescriptionId) {
    return this.patch(`/prescriptions/${prescriptionId}/fulfill`);
  }

  // ==================== PATIENT ENDPOINTS ====================

  /**
   * Get all patients
   */
  async getPatients(params = {}) {
    return this.get('/patients', params);
  }

  /**
   * Get patient by ID
   */
  async getPatientById(patientId) {
    return this.get(`/patients/${patientId}`);
  }

  /**
   * Get patient notes
   */
  async getPatientNotes(patientId) {
    return this.get(`/patients/${patientId}/notes`);
  }

  /**
   * Add patient note
   */
  async addPatientNote(patientId, noteData) {
    return this.post(`/patients/${patientId}/notes`, noteData);
  }

  /**
   * Get patient appointments
   */
  async getPatientAppointments(patientId) {
    return this.get(`/patients/${patientId}/appointments`);
  }

  // ==================== ADMIN ENDPOINTS ====================

  /**
   * Get admin statistics
   */
  async getAdminStats() {
    return this.get('/admin/stats');
  }

  /**
   * Get recent activity
   */
  async getRecentActivity(limit = 20) {
    return this.get('/admin/activity', { limit });
  }

  // ==================== APPROVAL ENDPOINTS ====================

  /**
   * Get pending physicians
   */
  async getPendingPhysicians() {
    return this.get('/approvals/physicians/pending');
  }

  /**
   * Get pending sponsors
   */
  async getPendingSponsors() {
    return this.get('/approvals/sponsors/pending');
  }

  /**
   * Approve physician
   */
  async approvePhysician(userId, physicianId) {
    return this.post(`/approvals/physicians/${userId}/approve`, { physicianId });
  }

  /**
   * Reject physician
   */
  async rejectPhysician(userId, physicianId, rejectionReason) {
    return this.post(`/approvals/physicians/${userId}/reject`, { 
      physicianId, 
      rejectionReason 
    });
  }

  /**
   * Approve sponsor
   */
  async approveSponsor(userId, sponsorId) {
    return this.post(`/approvals/sponsors/${userId}/approve`, { sponsorId });
  }

  /**
   * Reject sponsor
   */
  async rejectSponsor(userId, sponsorId, rejectionReason) {
    return this.post(`/approvals/sponsors/${userId}/reject`, { 
      sponsorId, 
      rejectionReason 
    });
  }

  /**
   * Get approval history
   */
  async getApprovalHistory(limit = 50) {
    return this.get('/approvals/history', { limit });
  }

  // ==================== ROLE ENDPOINTS ====================

  /**
   * Get all roles
   */
  async getAllRoles() {
    return this.get('/roles');
  }

  /**
   * Promote to super admin
   */
  async promoteToSuperAdmin(userId) {
    return this.post('/roles/promote-super-admin', { userId });
  }

  /**
   * Promote to global physician
   */
  async promoteToGlobalPhysician(userId) {
    return this.post('/roles/promote-global-physician', { userId });
  }

  /**
   * Demote user
   */
  async demoteUser(userId) {
    return this.post('/roles/demote', { userId });
  }

  /**
   * Get role change history
   */
  async getRoleChangeHistory(userId) {
    return this.get(`/roles/history/${userId}`);
  }

  // ==================== TENANT ENDPOINTS ====================

  /**
   * Get all tenants
   */
  async getTenants(params = {}) {
    return this.get('/tenants', params);
  }

  /**
   * Get tenant by ID
   */
  async getTenantById(tenantId) {
    return this.get(`/tenants/${tenantId}`);
  }

  /**
   * Create tenant
   */
  async createTenant(tenantData) {
    return this.post('/tenants', tenantData);
  }

  /**
   * Update tenant
   */
  async updateTenant(tenantId, updates) {
    return this.put(`/tenants/${tenantId}`, updates);
  }

  /**
   * Delete tenant
   */
  async deleteTenant(tenantId) {
    return this.delete(`/tenants/${tenantId}`);
  }

  /**
   * Activate tenant
   */
  async activateTenant(tenantId) {
    return this.patch(`/tenants/${tenantId}/activate`);
  }

  /**
   * Deactivate tenant
   */
  async deactivateTenant(tenantId) {
    return this.patch(`/tenants/${tenantId}/deactivate`);
  }

  /**
   * Get tenant users
   */
  async getTenantUsers(tenantId) {
    return this.get(`/tenants/${tenantId}/users`);
  }

  /**
   * Get tenant statistics
   */
  async getTenantStats(tenantId) {
    return this.get(`/tenants/${tenantId}/stats`);
  }
}

// Export singleton instance
const apiClient = new ApiClient();
export default apiClient;

